import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Repeat, Pause, Play, Trash2, XCircle } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { useToast } from "../../hooks/use-toast";
import {
  useRecurringSeries,
  type RecurringInstance,
} from "../../hooks/useRecurringSeries";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

interface RecurringSeriesManagerProps {
  pollId: string;
}

const STATUS_STYLES: Record<string, string> = {
  active: "bg-success/15 text-success border-success/30",
  cancelled: "bg-destructive/15 text-destructive border-destructive/30",
  archived: "bg-muted text-muted-foreground border-border",
};

/**
 * Creator-only management panel for a recurring series (results page):
 * pause/resume reminders+generation, cancel a single occurrence, delete the
 * whole series (archives the poll, hard-deletes votes — GDPR).
 */
export function RecurringSeriesManager({
  pollId,
}: RecurringSeriesManagerProps) {
  const { t, i18n } = useTranslation(["polls", "common"]);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: series } = useRecurringSeries(pollId);

  const [cancelTarget, setCancelTarget] = useState<RecurringInstance | null>(
    null,
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!series) return null;

  const paused = series.status === "paused";
  const locale = i18n.language.startsWith("fr")
    ? "fr-FR"
    : i18n.language.startsWith("es")
      ? "es-ES"
      : "en-US";

  const formatOccurrence = (iso: string) =>
    new Date(`${iso}T00:00:00Z`).toLocaleDateString(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["recurring-series", pollId] });

  const handlePauseResume = async () => {
    setBusy(true);
    try {
      const { error } = await supabase.rpc("set_recurring_series_status", {
        p_series_id: series.series_id,
        p_status: paused ? "active" : "paused",
      });
      if (error) throw error;
      await refresh();
      toast({
        title: paused
          ? t("polls:recurring.manage.resumed")
          : t("polls:recurring.manage.paused"),
      });
    } catch {
      toast({
        title: t("polls:recurring.manage.action_failed"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleCancelInstance = async () => {
    if (!cancelTarget) return;
    setBusy(true);
    try {
      const { error } = await supabase.rpc("cancel_recurring_instance", {
        p_instance_id: cancelTarget.id,
      });
      if (error) throw error;
      await refresh();
      toast({
        title: t("polls:recurring.manage.occurrence_cancelled", {
          date: formatOccurrence(cancelTarget.occurrence_date),
        }),
      });
    } catch {
      toast({
        title: t("polls:recurring.manage.action_failed"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setCancelTarget(null);
    }
  };

  const handleDeleteSeries = async () => {
    setBusy(true);
    try {
      const { error } = await supabase.rpc("delete_recurring_series", {
        p_series_id: series.series_id,
      });
      if (error) throw error;
      toast({ title: t("polls:recurring.manage.series_deleted") });
      navigate("/dashboard");
    } catch {
      toast({
        title: t("polls:recurring.manage.action_failed"),
        variant: "destructive",
      });
      setBusy(false);
      setShowDeleteDialog(false);
    }
  };

  return (
    <div
      className="bg-card border border-border rounded-lg p-4 space-y-4"
      data-testid="recurring-series-manager"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-foreground">
          <Repeat className="w-4 h-4 text-primary" />
          <span className="font-medium">
            {t("polls:recurring.manage.title")}
          </span>
          <span
            className={`text-xs px-2 py-0.5 rounded border ${
              paused
                ? "bg-warning/15 text-warning border-warning/30"
                : "bg-success/15 text-success border-success/30"
            }`}
            data-testid="recurring-series-status"
          >
            {paused
              ? t("polls:recurring.manage.status_paused")
              : t("polls:recurring.manage.status_active")}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handlePauseResume}
            disabled={busy}
            className="bg-muted hover:bg-accent text-foreground border border-border h-8 px-3 text-sm"
            data-testid="recurring-pause-resume"
          >
            {paused ? (
              <>
                <Play className="w-3.5 h-3.5 mr-1.5" />
                {t("polls:recurring.manage.resume")}
              </>
            ) : (
              <>
                <Pause className="w-3.5 h-3.5 mr-1.5" />
                {t("polls:recurring.manage.pause")}
              </>
            )}
          </Button>
          <Button
            onClick={() => setShowDeleteDialog(true)}
            disabled={busy}
            className="bg-destructive/15 hover:bg-destructive/25 text-destructive border border-destructive/30 h-8 px-3 text-sm"
            data-testid="recurring-delete-series"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5" />
            {t("polls:recurring.manage.delete")}
          </Button>
        </div>
      </div>

      {paused && (
        <p className="text-xs text-warning/80">
          {t("polls:recurring.manage.paused_hint")}
        </p>
      )}

      <div className="max-h-64 overflow-y-auto rounded border border-border">
        <table className="w-full text-sm">
          <tbody>
            {series.instances.map((instance) => (
              <tr
                key={instance.id}
                className="border-b border-border last:border-0"
                data-testid={`recurring-instance-${instance.position}`}
              >
                <td className="px-3 py-2 text-muted-foreground w-10">
                  {instance.position}
                </td>
                <td
                  className={`px-3 py-2 ${
                    instance.status === "cancelled"
                      ? "text-muted-foreground line-through"
                      : "text-foreground"
                  }`}
                >
                  {formatOccurrence(instance.occurrence_date)}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded border ${STATUS_STYLES[instance.status] ?? STATUS_STYLES.archived}`}
                  >
                    {t(`polls:recurring.manage.instance_${instance.status}`)}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {instance.status === "active" && (
                    <button
                      onClick={() => setCancelTarget(instance)}
                      disabled={busy}
                      className="inline-flex items-center gap-1 text-xs text-destructive hover:text-destructive/80"
                      data-testid={`recurring-cancel-${instance.position}`}
                    >
                      <XCircle className="w-3.5 h-3.5" />
                      {t("polls:recurring.manage.cancel_occurrence")}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation : annuler une occurrence */}
      <AlertDialog
        open={!!cancelTarget}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("polls:recurring.manage.cancel_dialog_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("polls:recurring.manage.cancel_dialog_desc", {
                date: cancelTarget
                  ? formatOccurrence(cancelTarget.occurrence_date)
                  : "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelInstance}
              disabled={busy}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              data-testid="confirm-cancel-occurrence"
            >
              {t("polls:recurring.manage.cancel_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation : supprimer la série */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("polls:recurring.manage.delete_dialog_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("polls:recurring.manage.delete_dialog_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>
              {t("common:cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSeries}
              disabled={busy}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              data-testid="confirm-delete-series"
            >
              {t("common:delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default RecurringSeriesManager;
